import {createFetchClientWithAuth} from '../util/request/middleware'
import {camelCase as camelCaseObject} from 'change-case-object'
import moment from 'moment'

const client = createFetchClientWithAuth('v1/budget')
const clientV2 = createFetchClientWithAuth('v2/budget')

/**
 * Returns the budget health for the authenticated client
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetBudgetHealth
 * @returns {Number}
 */
export async function health(){
	return (await client.get('health')).HealthLevel
}

/**
 * Returns the available transfer destinations for the authenticated client
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetTransferDestinations
 * @return {Object}
 */
export async function getTransferDestinations(){
	return camelCaseObject(await clientV2.get('transferDestinations'))
}

/**
 * Returns the withdrawal service status for the authenticated client
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetServiceStatus
 * @return {Object}
 */
export async function getWithdrawalServiceStatus(){
	return camelCaseObject(await client.get('withdrawalServiceStatus'))
}

/**
 * Submits a transfer money request
 * @param sourceId
 * @param bsb
 * @param accountNumber
 * @param description
 * @param amount
 * @return {String|Object}
 */
export async function transferMoney(sourceId, bsb, accountNumber, destinationId, description, amount){
	const response = await client.post('transferMoney', {
		body: {
			SourceId: sourceId,
			DestinationId: destinationId,
			BSB: bsb,
			AccountNumber: accountNumber,
			Description: description,
			Amount: amount
		}
	})

	return response.ClientNotificationId
}

/**
 * Returns the pending transfers for the authenticated client
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_TransferMoney
 * @return {Array}
 */
export async function getPendingTransfers(){
	const response = await client.get('pendingTransfers')
	return camelCaseObject(response.filter((item) => item.Amount !== 0))
}

/**
 * Cancels a pending transfer
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_CancelPendingTransfer
 * @param transferId
 * @return {String}
 */
export async function cancelPendingTransfer(transferId){
	return (await client.post(`pendingTransfers/${transferId}/cancel/`)).ClientNotificationId
}

/**
 * Returns upcoming activities for the current client. Activities are paginated by a count and offset.
 * If the upcoming activities have changed since the last request, the offset is ignored and the requested count is
 * returned from offset 0 (as there could be records inserted before the requested offset). The ActivitiesUpdated flag
 * on the results indicates whether this has occurred.
 * The cached results expire after an hour, so requests after that will be treated as fresh requests.
 * Note: The cache is only used to compare the activities being returned from the legacy service - we will never
 * return cached results in case they are incorrect.
 *
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetUpcomingActivity
 * @param count
 * @param offset
 * @return {Object}
 */
export async function getUpcomingActivity(count, offset){
	return camelCaseObject(
		await client.get('upcomingActivity', {
			query: {
				count: count,
				offset: offset
			}
		})
	)
}

/**
 * Returns on hold BPay payments
 *
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetUpcomingBPayActivity
 * @return {Object}
 */
export async function getUpcomingActivityBPay(){
	return camelCaseObject(await client.get('upcomingActivity/bpay')).activities.map((item) => {
		item.runningBalance = false

		return item
	})
}

/**
 * Updates an income
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_UpdateExpectedIncome
 * @param id
 * @param amount
 * @param dateExpected
 * @returns {Promise<number>|Promise.<number>}
 */
export async function updateIncome(id, amount, dateExpected){
	const result = await client.post(`income/${id}/update`, {
		body: {
			ExpectedIncomeAmount: amount,
			ExpectedIncomeDate: moment(dateExpected).format('YYYY-MM-DD[T]HH:mm:ss')
		}
	})

	return result.ClientNotificationId
}

/**
 * Verify Income
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_VerifyExpectedIncome
 * @param id
 * @returns {Promise<number>|Promise.<number>}
 */
export async function verifyIncome(id){
	return (await client.post(`income/${id}/verify`)).ClientNotificationId
}

/**
 * Fetch history
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetHistory
 * @param month
 * @param year
 */
export async function getHistory(month, year){
	return camelCaseObject(await client.get(`history`, {
		query: {
			Month: month,
			Year: year
		}
	})).reverse()
}

/**
 * Returns on hold Income payments
 *
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetUpcomingIncomeActivity
 * @return {Object}
 */
export async function getUpcomingActivityIncome(){
	return camelCaseObject(await client.get('upcomingActivity/income')).activities.map((item) => {
		item.runningBalance = false

		return item
	})
}


/**
 * Returns on 12 month report
 *
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetBudget
 * @return {Object}
 */
export async function getTwelveMonthBudget(){
	const report = camelCaseObject(await client.get())
	const summary = {}

	// Key summary by name
	for(let row of report.summaries){
		summary[row.name] = row
	}

	report.summaries = summary

	// Find total expenses and incomes
	report.summaries.incomes = report.budget.find((stream) => stream.description === 'Total Income').amounts
	report.summaries.expenses = [0,0,0,0,0,0,0,0,0,0,0,0]

	for(let row of report.budget){
		if(row.category.startsWith('EXP')){
			row.amounts.forEach((amount, index) => report.summaries.expenses[index] += amount)
		}
	}

	return report
}

/**
 * Returns on hold payments
 *
 * @link http://developmentmybudgetapi.azurewebsites.net/swagger/ui/index#!/Budget/Budget_GetUpcomingBPayActivity
 * @return {Object}
 */
export async function getUpcomingActivityOnHold(){
	return camelCaseObject(await client.get('upcomingActivity/onhold')).activities.map((item) => {
		item.runningBalance = false

		return item
	})
}




// WEBPACK FOOTER //
// ./node_modules/mb-app-common/lib/api/budget.js